// =============================================================================
// components/chat/AudioMessageBubble.tsx — play/pause voice-message bubble (DM).
//
// MIGRATION (bare RN, P3 DMs / blueprint §1 swap #11): ported off the Expo
// app's expo-audio version. expo-audio's useAudioPlayer/useAudioPlayerStatus
// hooks → the imperative singleton in services/audio (react-native-audio-recorder-
// player), driven with plain React state. @expo/vector-icons → ../ui/Icon.
//
// Single-playback coordination is UNCHANGED: the parent passes activeId +
// onActivate; this bubble claims the active slot when it starts and pauses itself
// whenever it's no longer the active bubble. The audio lib is itself a singleton
// (one stream at a time), so claiming the slot + the lib's own stop-on-new-play
// both enforce one-at-a-time.
//
// Crash-safety: the source URL may be missing/blank → disabled control. All
// playback calls are no-ops if the native lib isn't installed yet (pre-pod).
// =============================================================================
import { useEffect, useRef, useState } from 'react';
import { View, Text, TouchableOpacity, StyleSheet } from 'react-native';
import { Ionicons } from '../ui/Icon';
import { playback } from '../../services/audio';
import { colors, spacing, fonts } from '../../constants/theme';

// Seconds → "m:ss" (e.g. 75 → "1:15"); guards NaN/negative.
function formatDuration(secs?: number): string {
  const s = Number.isFinite(secs) && (secs as number) > 0 ? Math.floor(secs as number) : 0;
  const m = Math.floor(s / 60);
  const r = (s % 60).toString().padStart(2, '0');
  return `${m}:${r}`;
}

interface Props {
  uri: string;
  // Total length in seconds reported by the sender (fallback before the player
  // resolves its own duration).
  durationSeconds?: number;
  isMine: boolean;
  // Stable id used to coordinate single-playback across bubbles.
  bubbleId: string;
  activeId: string | null;
  onActivate: (id: string | null) => void;
}

export default function AudioMessageBubble({
  uri,
  durationSeconds,
  isMine,
  bubbleId,
  activeId,
  onActivate,
}: Props) {
  const [isPlaying, setIsPlaying] = useState(false);
  // Live position/duration in SECONDS (the audio adapter reports millis).
  const [elapsed, setElapsed] = useState(0);
  const [resolvedDuration, setResolvedDuration] = useState(0);
  const isActive = activeId === bubbleId;

  // If another bubble became active, stop ourselves (we were playing).
  useEffect(() => {
    if (!isActive && isPlaying) {
      playback.stop().catch(() => {});
      setIsPlaying(false);
    }
  }, [isActive, isPlaying]);

  // Stop playback if this bubble unmounts mid-play (FlatList recycling).
  const playingRef = useRef(false);
  playingRef.current = isPlaying;
  useEffect(() => {
    return () => {
      if (playingRef.current) playback.stop().catch(() => {});
    };
  }, []);

  const tint = isMine ? colors.white : colors.primary;
  const trackColor = isMine ? 'rgba(255,255,255,0.35)' : 'rgba(108,92,231,0.25)';

  // Prefer the player's resolved duration; fall back to the sender-reported one.
  const total = (resolvedDuration > 0 ? resolvedDuration : durationSeconds) || 0;
  const progress = total > 0 ? Math.min(1, elapsed / total) : 0;
  const label = isPlaying ? formatDuration(elapsed) : formatDuration(total);

  async function togglePlay() {
    if (!uri) return;
    if (isPlaying) {
      await playback.stop().catch(() => {});
      setIsPlaying(false);
      onActivate(null);
      return;
    }
    // Claim the active slot first so any other bubble pauses.
    onActivate(bubbleId);
    setElapsed(0);
    const ok = await playback.play(uri, {
      onProgress: ({ currentMillis, durationMillis }) => {
        setElapsed(currentMillis / 1000);
        if (durationMillis > 0) setResolvedDuration(durationMillis / 1000);
      },
      onEnd: () => {
        setIsPlaying(false);
        setElapsed(0);
        onActivate(null);
        playback.stop().catch(() => {});
      },
    });
    if (ok) {
      setIsPlaying(true);
    } else {
      // Playback failed (bad url / lib absent); release the slot.
      onActivate(null);
    }
  }

  return (
    <View style={styles.row}>
      <TouchableOpacity
        onPress={togglePlay}
        disabled={!uri}
        style={[styles.playBtn, { borderColor: tint }]}
        accessibilityRole="button"
        accessibilityLabel={isPlaying ? 'Pause voice message' : 'Play voice message'}
      >
        <Ionicons
          name={isPlaying ? 'pause' : 'play'}
          size={18}
          color={tint}
          style={!isPlaying ? { marginLeft: 2 } : undefined}
        />
      </TouchableOpacity>
      <View style={styles.body}>
        <View style={[styles.track, { backgroundColor: trackColor }]}>
          <View
            style={[
              styles.fill,
              { backgroundColor: tint, width: `${Math.round(progress * 100)}%` },
            ]}
          />
        </View>
        <Text style={[styles.time, { color: isMine ? 'rgba(255,255,255,0.85)' : colors.ink2 }]}>
          {label}
        </Text>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    minWidth: 160,
  },
  playBtn: {
    width: 34,
    height: 34,
    borderRadius: 17,
    borderWidth: 1.5,
    alignItems: 'center',
    justifyContent: 'center',
  },
  body: {
    flex: 1,
    gap: spacing.xs,
  },
  track: {
    height: 4,
    borderRadius: 2,
    overflow: 'hidden',
  },
  fill: {
    height: 4,
    borderRadius: 2,
  },
  time: {
    fontSize: 11,
    ...fonts.medium,
  },
});
